"use client"
/* */
import { getProperty } from 'dot-prop';
import { customPropertyPrefix } from '../types.mjs';
import { unique, difference } from 'remeda';
import { FilterValue } from './FilterValue.mjs';
import z from 'zod';
import { toArray } from '../../../../lib/array/toArray.mjs';

const equalsPropertyMatcher = (filterValue, propertyValue) => filterValue === propertyValue;
const stringCastRenderMethod = (value) => String(value);
class Filter {
  static settingsStorageSchema = z.record(z.array(z.string())).optional();
  _values;
  _valuesFromTableState;
  list;
  property;
  mode;
  matcher;
  renderItem;
  name;
  onFilterUpdateCallbacks = /* @__PURE__ */ new Set();
  defaultSelectedValues;
  constructor(list, shape) {
    this.list = list;
    this.property = shape.property;
    this.mode = shape.mode ?? "one";
    this._values = shape.values?.map((v) => FilterValue.create(this, v));
    this.matcher = shape.matcher ?? equalsPropertyMatcher;
    this.renderItem = shape.renderItem ?? stringCastRenderMethod;
    this.name = shape.name;
    this.defaultSelectedValues = shape.defaultSelected;
  }
  getStoredSelectedIds() {
    return this.list.getStoredFilterDefaultSettings()?.[String(this.property)];
  }
  updateInitialState(initialState) {
    const initialValues = this.getInitialValues();
    if (initialValues?.length) {
      initialState.columnFilters = [
        ...initialState.columnFilters ?? [],
        {
          id: this.property,
          value: initialValues
        }
      ];
    }
  }
  updateTableColumnDef(def) {
    def.enableColumnFilter = true;
    def.filterFn = this.getReactTableFilterFn();
  }
  getReactTableFilterFn() {
    return (row, _, filterValue) => {
      const propertyAsString = this.property;
      const filterBy = propertyAsString.startsWith(customPropertyPrefix) ? row.original : getProperty(row.original, propertyAsString);
      return this.checkFilterMatches(filterBy, filterValue);
    };
  }
  checkFilterMatches(property, filterValueInput) {
    if (filterValueInput === null) {
      return true;
    }
    const predicate = (filterValue) => this.matcher(filterValue.value, property);
    const toFilterValue = (something) => FilterValue.create(this, something);
    if (this.mode === "all") {
      return toArray(filterValueInput).map(toFilterValue).every(predicate);
    } else if (this.mode === "some") {
      const filterArr = toArray(filterValueInput);
      return filterArr.length === 0 || filterArr.map(toFilterValue).some(predicate);
    } else if (this.mode === "one") {
      return predicate(toFilterValue(filterValueInput));
    }
    throw new Error(`Unknown filter mode '${this.mode}'`);
  }
  getTableColumnFilter() {
    return this.list.reactTable.table.getState().columnFilters.find((f) => f.id === this.property);
  }
  getTableColumn() {
    return this.list.reactTable.getTableColumn(this.property);
  }
  getValue() {
    return this.getTableColumnFilter()?.value ?? null;
  }
  getValuesFromTableState() {
    return unique(
      Array.from(this.getTableColumn().getFacetedUniqueValues().keys()).flatMap((v) => v).filter((v) => v !== void 0 && v !== null)
    ).map((v) => FilterValue.create(this, v));
  }
  checkIfValueIsUnknown(value) {
    const isKnown = this.values.some((v) => v.id === value.id);
    return !isKnown;
  }
  deleteUnknownFilterValues() {
    if (this.values === this.valuesFromTableState) {
      return;
    }
    for (const currentValues of this.getArrayValue()) {
      if (this.checkIfValueIsUnknown(currentValues)) {
        this.deactivateValue(currentValues);
      }
    }
  }
  get values() {
    return this._values ?? this.valuesFromTableState;
  }
  get valuesFromTableState() {
    if (!this._valuesFromTableState) {
      this._valuesFromTableState = this.getValuesFromTableState();
    }
    return this._valuesFromTableState;
  }
  getArrayValue() {
    const value = this.getValue();
    return value === null ? [] : toArray(value).map((v) => FilterValue.create(this, v));
  }
  isValueActive(value) {
    return this.getArrayValue().some((v) => v.equals(value));
  }
  isActive() {
    return this.getArrayValue().length > 0;
  }
  deactivateValue(value) {
    const currentValueAsArray = this.getArrayValue();
    let updatedValue;
    if (this.mode === "all" || this.mode === "some") {
      updatedValue = currentValueAsArray.filter((v) => !v.equals(value));
    } else {
      updatedValue = null;
    }
    this.list.reactTable.getTableColumn(this.property).setFilterValue(updatedValue);
    this.onFilterUpdateCallbacks.forEach((cb) => cb());
  }
  hasChanged() {
    const currentValues = this.getArrayValue().map((v) => v.value);
    const initialValues = this.getInitialFilterValues()?.map((v) => v.value) ?? [];
    return currentValues.length !== initialValues.length || difference(currentValues, initialValues).length > 0;
  }
  getInitialValues() {
    return this.getStoredSelectedIds() ?? this.defaultSelectedValues;
  }
  getInitialFilterValues() {
    return this.getInitialValues()?.map((v) => FilterValue.create(this, v));
  }
  resetValues() {
    let resetTo;
    const initialValues = this.getInitialValues();
    if (initialValues) {
      resetTo = initialValues;
    } else {
      if (this.mode === "all" || this.mode === "some") {
        resetTo = [];
      } else {
        resetTo = null;
      }
    }
    this.list.reactTable.getTableColumn(this.property).setFilterValue(resetTo);
    this.onFilterUpdateCallbacks.forEach((cb) => cb());
  }
  clear() {
    this.list.reactTable.getTableColumn(this.property).setFilterValue(null);
    this.onFilterUpdateCallbacks.forEach((cb) => cb());
  }
  toggleValue(newValue) {
    const currentValueAsArray = this.getArrayValue();
    let updatedValue;
    if (this.mode === "all" || this.mode === "some") {
      if (newValue.isActive) {
        updatedValue = currentValueAsArray.filter((v) => !v.equals(newValue));
      } else {
        updatedValue = [...currentValueAsArray, newValue];
      }
    } else {
      updatedValue = newValue.isActive ? null : newValue;
    }
    this.list.reactTable.getTableColumn(this.property).setFilterValue(updatedValue);
    this.onFilterUpdateCallbacks.forEach((cb) => cb());
  }
  onFilterUpdated(cb) {
    this.onFilterUpdateCallbacks.add(cb);
  }
}

export { Filter };
//# sourceMappingURL=Filter.mjs.map
